import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Radio,
  RadioGroup,
  Select,
} from '@material-ui/core';
import React, { useEffect } from 'react';
import { MenuItem } from '@material-ui/core';
import { makeStyles, Paper, TextField } from '@material-ui/core';
import { Field, Form } from 'react-final-form';
import {
  composeValidators,
  requiredFn,
  emailFn,
  roles,
  countries,
  states,
} from './utils';
import { useAnswerContext } from '../../context';
import { submitAssessment } from '../../api';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '30px',
  },
  formStyle: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: '30px',
  },
  paper: {
    boxShadow: 'rgb(0 0 0 / 10%) 0px 2px 13px 0px',
    [theme.breakpoints.down('sm')]: {
      marginTop: 20,
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: 40,
    },
    maxWidth: '90%',
    width: 900,
    marginBottom: 90,
    borderRadius: 15,
  },
  accordin: {
    maxWidth: 640,
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignContent: 'center',
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      marginBottom: 20,
    },
    [theme.breakpoints.up('sm')]: {
      marginBottom: 50,
    },
  },
  header: {
    fontWeight: 400,
    textAlign: 'center',
    color: 'rgb(32, 33, 36)',
    margin: '20px auto',
    [theme.breakpoints.down('sm')]: {
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '0px',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '34px',
      lineHeight: '50px',
      letterSpacing: '-0.39px',
    },
  },
  images: {
    [theme.breakpoints.up('sm')]: {
      marginTop: 70,
      marginBottom: 30,
    },
    display: 'flex',
    justifyContent: 'center',
  },
  startButton: {
    [theme.breakpoints.down('xs')]: {
      marginTop: 15,
    },
  },
  image: {
    [theme.breakpoints.down('xs')]: {
      width: 60,
      height: 60,
    },
    [theme.breakpoints.up('xs')]: {
      width: 80,
      height: 80,
    },
    [theme.breakpoints.up('sm')]: {
      width: 120,
      height: 120,
    },
  },
  text: {
    fontSize: '18px',
    lineHeight: '30px',
    letterSpacing: '0.2px',
    textAlign: 'center',
    marginTop: 0,
  },
  footer: {
    display: 'flex',
    flexWrap: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    marginTop: 10,
  },
  radioLabel: {
    flex: 1,
    fontSize: '16px',
    lineHeight: '30px',
    letterSpacing: '0.2px',
    marginTop: 20,
  },
}));

const fieldInputBuilder = (
  name: string,
  label: string,
  ...validators: Array<(value: string) => undefined | string>
) => (
  <Field
    name={name}
    validate={
      validators ? composeValidators(requiredFn, ...validators) : requiredFn
    }
  >
    {({ input, meta }) => (
      <TextField
        error={meta.error && meta.touched}
        label={label}
        variant="filled"
        {...input}
        required
        fullWidth
        helperText={meta.error && meta.touched ? meta.error : ''}
      />
    )}
  </Field>
);

const FieldSelectBuilder: React.FC<{
  name: string;
  label: string;
  options: Array<{ name: string; code: string }>;
}> = ({ name, label, options }) => {
  return (
    <Field name={name} validate={requiredFn}>
      {({ input, meta }) => (
        <FormControl variant="filled" error={meta.error && meta.touched}>
          <InputLabel id={name}>{label}*</InputLabel>
          <Select labelId={name} {...input} fullWidth>
            {options.map(({ code, name }) => (
              <MenuItem key={code} value={code}>
                {name}
              </MenuItem>
            ))}
          </Select>
          {meta.error && meta.touched ? (
            <FormHelperText>{meta.error}</FormHelperText>
          ) : null}
        </FormControl>
      )}
    </Field>
  );
};

export const MainFrom = () => {
  const { startAssessment } = useAnswerContext();
  const {
    root,
    formStyle,
    paper,
    accordin,
    header,
    text,
    footer,
    images,
    radioLabel,
    startButton,
    image,
  } = useStyles();
  const history = useHistory<any>();

  useEffect(() => {
    if (!history.location.state?.form) {
      const search = history.location.search;
      history.push(
        history.location.pathname
          ? history.location.pathname + search
          : '/' + search,
        { form: true },
      );
    }
  }, [history]);

  return (
    <Paper className={paper}>
      <div className={root}>
        <div className={accordin}>
          <div className={images}>
            <img
              className={image}
              src={process.env.PUBLIC_URL + '/images/form1.png'}
              alt="figure 1"
            />
            <img
              className={image}
              src={process.env.PUBLIC_URL + '/images/form2.png'}
              alt="figure 2"
            />
            <img
              className={image}
              src={process.env.PUBLIC_URL + '/images/form3.png'}
              alt="figure 3"
            />
            <img
              className={image}
              src={process.env.PUBLIC_URL + '/images/form4.png'}
              alt="figure 4"
            />
          </div>
          <h2 className={header}>
            Harness the power of a data cloud and start planning for change,
            now.
          </h2>
          <p className={text}>
            Data is only as powerful as the way it's put to work. Fill out the
            form to learn where you are on the journey towards a data-driven
            culture.
          </p>
        </div>
        <Form
          onSubmit={(data: any) => {
            submitAssessment(data, null);
            startAssessment();
          }}
          render={({ handleSubmit, values }) => {
            const countryCode = values['CountryCode'];
            const stateData = states.find((x) => x.code === countryCode);
            return (
              <form onSubmit={handleSubmit}>
                <div className={formStyle}>
                  {fieldInputBuilder('FirstName', 'First name')}
                  {fieldInputBuilder('LastName', 'Last name')}
                  {fieldInputBuilder('Company', 'Business name')}
                  {fieldInputBuilder('Email', 'Email', emailFn)}
                  {fieldInputBuilder('Title', 'Job title')}
                  <FieldSelectBuilder
                    name="Job_Role__c"
                    label="Job role"
                    options={roles.map((x) => ({ name: x, code: x }))}
                  />
                  {fieldInputBuilder('Phone', 'Business mobile phone')}
                  <FieldSelectBuilder
                    name="CountryCode"
                    label="Country"
                    options={countries}
                  />

                  {stateData ? (
                    <FieldSelectBuilder
                      name="StateCode"
                      label={stateData.name}
                      options={stateData.state}
                    />
                  ) : null}
                </div>
                <div className={radioLabel}>
                  Sign me up to receive news, product updates, event information
                  and special offers about Google Cloud from Google. I
                  understand my personal data will be processed in accordance
                  with Google’s{' '}
                  <a href="https://policies.google.com/privacy" target="blank">
                    Privacy Policy.
                  </a>
                </div>
                <div className={footer}>
                  <Field
                    name="Opt_In__c"
                    validate={(val: string) =>
                      !val ? 'This field is required' : undefined
                    }
                  >
                    {({ input, meta }) => (
                      <div
                        style={{
                          position: 'relative',
                          alignSelf: 'flex-start',
                        }}
                      >
                        <RadioGroup
                          row
                          aria-label="position"
                          {...input}
                          style={{
                            display: 'flex',
                          }}
                        >
                          <FormControlLabel
                            value="Yes"
                            control={<Radio color="primary" />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="No"
                            control={<Radio color="primary" />}
                            label="No"
                          />
                        </RadioGroup>
                        {meta.error && meta.submitFailed ? (
                          <FormHelperText
                            style={{
                              position: 'absolute',
                              bottom: '-14px',
                              left: 0,
                              whiteSpace: 'nowrap',
                            }}
                            error
                          >
                            {meta.error}
                          </FormHelperText>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    className={startButton}
                    style={{ alignSelf: 'center', whiteSpace: 'nowrap' }}
                  >
                    Start assessment
                  </Button>
                </div>
              </form>
            );
          }}
        />
      </div>
    </Paper>
  );
};
